import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import {
  buildMenuQueryParams,
  extractMenuArray,
  getSelectedMenuContext,
  getUserVendorCategoryIdx,
  normalizeBoolean,
  normalizeMenuItems,
} from '../../../../../utils/menu';

// API 응답 데이터 타입 정의
interface MenuDto {
  menuIdx: number;
  name: string;
  path: string;
  icon: string;
  vendorCategoryIdx?: number | null;
  parentMenuIdx?: number | null;
  orderNo: number;
  isActive: string; // 'y' or 'n'
}

// 폼 입력 데이터 타입
interface MenuFormData {
  name: string;
  path: string;
  icon: string;
  orderNo: number;
  isActive: boolean;
}

const USER_INFO_UPDATED_EVENT = 'wedmin:user-info-updated';

export default function MenuManagement() {
  // 상태 관리
  const [menus, setMenus] = useState<MenuDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // 모달 관리
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedMenuIdx, setSelectedMenuIdx] = useState<number | null>(null);

  // 폼 데이터 상태 (parentMenuIdx 제거)
  const [formData, setFormData] = useState<MenuFormData>({
    name: '',
    path: '',
    icon: '',
    orderNo: 0,
    isActive: true
  });

  // --- [API] 1. 메뉴 목록 조회 (GET) ---
  const fetchMenus = useCallback(async () => {
    setIsLoading(true);
    try {
      const userStr = localStorage.getItem('userInfo');
      const user = userStr ? JSON.parse(userStr) : null;
      const vendorCategoryIdx = getUserVendorCategoryIdx(user);
      const selectedContext = getSelectedMenuContext(user);
      const token = localStorage.getItem('accessToken');
      const query = buildMenuQueryParams({
        vendorCategoryIdx,
        isGuest: selectedContext?.isGuestContext ?? false,
        includeInactive: true,
      });
      
      const response = await fetch(`https://api.wedmin.biz/menu${query}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();

      const rows = normalizeMenuItems(extractMenuArray(result));
      const normalized: MenuDto[] = rows.map((m) => ({
        menuIdx: m.menuIdx,
        name: m.name,
        path: m.path || '',
        icon: m.icon || '',
        vendorCategoryIdx: m.vendorCategoryIdx ?? null,
        parentMenuIdx: m.parentMenuIdx ?? null,
        orderNo: m.orderNo ?? 0,
        isActive: normalizeBoolean(m.isActive ?? true) ? 'y' : 'n',
      }));
      setMenus(normalized);
    } catch (error) {
      console.error('Fetch Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  useEffect(() => {
    const handleUserInfoUpdate = () => {
      fetchMenus();
    };

    window.addEventListener(USER_INFO_UPDATED_EVENT, handleUserInfoUpdate);
    return () => {
      window.removeEventListener(USER_INFO_UPDATED_EVENT, handleUserInfoUpdate);
    };
  }, [fetchMenus]);

  // --- [API] 2 & 3. 메뉴 생성(POST) 및 수정(PATCH) ---
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    const token = localStorage.getItem('accessToken');
    const url = isEditMode && selectedMenuIdx 
      ? `https://api.wedmin.biz/menu/${selectedMenuIdx}` 
      : `https://api.wedmin.biz/menu`;
    
    const method = isEditMode ? 'PATCH' : 'POST';

    // 상위 선택 UI가 없으므로 기본은 루트(null)로 전송
    const body = {
      name: formData.name,
      path: formData.path,
      icon: formData.icon,
      parentMenuIdx: null,
      orderNo: Number(formData.orderNo),
      isActive: formData.isActive
    };

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();
      if (response.ok) {
        alert(isEditMode ? '메뉴가 수정되었습니다.' : '메뉴가 생성되었습니다.');
        setShowMenuModal(false);
        fetchMenus();
      } else {
        alert(`저장 실패: ${result.message}`);
      }
    } catch (error) {
      console.error('Save Error:', error);
      alert('오류가 발생했습니다.');
    }
  };

  // --- [API] 4. 메뉴 삭제 (DELETE) ---
  const handleDelete = async (menuIdx: number) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/menu/${menuIdx}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('메뉴가 삭제되었습니다.');
        fetchMenus();
      } else {
        const result = await response.json();
        alert(`삭제 실패: ${result.message}`);
      }
    } catch (error) {
      console.error('Delete Error:', error);
    }
  };

  // --- 핸들러 ---
  const openAddModal = () => {
    setIsEditMode(false);
    setSelectedMenuIdx(null);
    setFormData({
      name: '',
      path: '',
      icon: '',
      orderNo: menus.length + 1,
      isActive: true
    });
    setShowMenuModal(true);
  };

  const openEditModal = (menu: MenuDto) => {
    setIsEditMode(true);
    setSelectedMenuIdx(menu.menuIdx);
    setFormData({
      name: menu.name,
      path: menu.path,
      icon: menu.icon,
      orderNo: menu.orderNo,
      isActive: menu.isActive === 'y'
    });
    setShowMenuModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">메뉴관리</h1>
            <div className="flex gap-3">
              <button
                onClick={openAddModal}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex items-center"
              >
                <i className="ri-add-line mr-2"></i>
                메뉴 추가
              </button>
            </div>
          </div>

          {/* 메뉴 목록 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">메뉴 목록</h2>
              {isLoading && <span className="text-sm text-gray-500">로딩중...</span>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">순서</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">메뉴명</th>
                    {/* 상위 메뉴 컬럼 삭제됨 */}
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">경로</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">아이콘</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">상태</th>
                    <th className="px-6 py-3 text-center text-sm font-medium text-gray-700">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {menus.length === 0 && !isLoading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-500">등록된 메뉴가 없습니다.</td>
                    </tr>
                  ) : (
                    menus.map((menu) => (
                      <tr key={menu.menuIdx} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">{menu.orderNo}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <i className={`${menu.icon} text-gray-500 text-lg`}></i>
                            <span className="text-sm font-medium text-gray-900">{menu.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{menu.path}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 font-mono">{menu.icon}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            menu.isActive === 'y' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {menu.isActive === 'y' ? '활성' : '비활성'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => openEditModal(menu)}
                              className="text-blue-600 hover:text-blue-800 w-8 h-8 flex items-center justify-center rounded hover:bg-blue-50 transition-colors"
                              title="수정"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(menu.menuIdx)}
                              className="text-red-600 hover:text-red-800 w-8 h-8 flex items-center justify-center rounded hover:bg-red-50 transition-colors"
                              title="삭제"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* 메뉴 상세(등록/수정) 팝업 Modal */}
      {showMenuModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-xl">
            <div className="flex justify-between items-center mb-6 border-b pb-4">
              <h2 className="text-xl font-bold text-gray-800">
                {isEditMode ? '메뉴 수정' : '메뉴 생성'}
              </h2>
              <button
                onClick={() => setShowMenuModal(false)}
                className="text-gray-500 hover:text-gray-700 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <form onSubmit={handleSave} className="space-y-4">
              {/* 메뉴명 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">메뉴명 <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="예: 고객 관리"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                />
              </div>

              {/* 경로 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">경로</label>
                <input 
                  type="text" 
                  name="path"
                  value={formData.path}
                  onChange={handleInputChange}
                  placeholder="예: /dashboard/customers"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                />
              </div>

              {/* 아이콘 */}
              <div>
                <div className="flex justify-between">
                  <label className="block text-sm font-medium text-gray-700 mb-1">아이콘 (Remix Icon)</label>
                  <a href="https://remixicon.com/" target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">아이콘 찾기</a>
                </div>
                <div className="relative">
                  <input 
                    type="text" 
                    name="icon"
                    value={formData.icon}
                    onChange={handleInputChange}
                    placeholder="예: ri-user-line"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 pl-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                  />
                  <div className="absolute left-3 top-2.5 text-gray-500">
                    <i className={formData.icon || 'ri-question-line'}></i>
                  </div>
                </div>
              </div>

              {/* 상위 메뉴 선택 박스 삭제됨 */}

              {/* 순서 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">순서</label>
                <input 
                  type="number" 
                  name="orderNo"
                  value={formData.orderNo}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                />
              </div>

              {/* 활성 상태 */}
              <div className="flex items-center pt-2">
                <input 
                  id="isActive"
                  type="checkbox" 
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer" 
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900 cursor-pointer">
                  메뉴 활성화
                </label>
              </div>
              
              {/* 버튼 액션 */}
              <div className="flex justify-end gap-3 mt-8 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowMenuModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors font-medium"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium shadow-sm"
                >
                  {isEditMode ? '수정 완료' : '메뉴 생성'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
